/** Search, filter, sort, and view controls for an event gallery. */
import { useId } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  IconSearch,
  IconLayoutGrid,
  IconList,
  IconSortDescending,
  IconSortAscending,
} from '@tabler/icons-react';
import { useGalleryStore } from '../../state/galleryStore.ts';
import { cn } from '../../lib/cn.ts';

export function SearchFilterBar() {
  // Shallow-select the fields + actions this bar uses (actions are stable),
  // so it doesn't re-render on unrelated store changes like selection toggles.
  const store = useGalleryStore(
    useShallow((s) => ({
      search: s.search,
      mediaType: s.mediaType,
      dateRange: s.dateRange,
      sortBy: s.sortBy,
      sortDescending: s.sortDescending,
      viewMode: s.viewMode,
      setSearch: s.setSearch,
      setMediaType: s.setMediaType,
      setDateRange: s.setDateRange,
      setSortBy: s.setSortBy,
      toggleSortDirection: s.toggleSortDirection,
      setViewMode: s.setViewMode,
    })),
  );
  const searchId = useId();

  return (
    <div className="border border-line bg-surface shadow-sm flex flex-wrap items-center gap-2 rounded-2xl p-2">
      {/* Search */}
      <div className="relative min-w-[12rem] flex-1">
        <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
        <input
          id={searchId}
          type="search"
          value={store.search}
          onChange={(e) => store.setSearch(e.target.value)}
          placeholder="Search by name…"
          aria-label="Search photos by name"
          className="h-9 w-full rounded-xl bg-grid pl-9 pr-3 text-sm outline-none placeholder:text-muted"
        />
      </div>

      {/* Media type */}
      <select
        value={store.mediaType}
        onChange={(e) => store.setMediaType(e.target.value as typeof store.mediaType)}
        aria-label="Filter by media type"
        className="h-9 rounded-xl bg-grid px-2 text-sm outline-none"
      >
        <option value="all">All</option>
        <option value="images">Images</option>
        <option value="videos">Videos</option>
      </select>

      {/* Date range */}
      <input
        type="date"
        value={store.dateRange.from}
        onChange={(e) => store.setDateRange({ ...store.dateRange, from: e.target.value })}
        aria-label="From date"
        className="h-9 rounded-xl bg-grid px-2 text-sm outline-none"
      />
      <input
        type="date"
        value={store.dateRange.to}
        onChange={(e) => store.setDateRange({ ...store.dateRange, to: e.target.value })}
        aria-label="To date"
        className="h-9 rounded-xl bg-grid px-2 text-sm outline-none"
      />

      {/* Sort */}
      <select
        value={store.sortBy}
        onChange={(e) => store.setSortBy(e.target.value as typeof store.sortBy)}
        aria-label="Sort by"
        className="h-9 rounded-xl bg-grid px-2 text-sm outline-none"
      >
        <option value="date">Date</option>
        <option value="name">Name</option>
        <option value="size">Size</option>
      </select>
      <button
        onClick={store.toggleSortDirection}
        aria-label={store.sortDescending ? 'Sort ascending' : 'Sort descending'}
        title={store.sortDescending ? 'Descending' : 'Ascending'}
        className="grid h-9 w-9 place-items-center rounded-xl bg-grid text-fg-2 hover:text-fg"
      >
        {store.sortDescending ? (
          <IconSortDescending className="h-4 w-4" />
        ) : (
          <IconSortAscending className="h-4 w-4" />
        )}
      </button>

      {/* View mode */}
      <div className="flex rounded-xl bg-grid p-0.5">
        <ViewButton
          active={store.viewMode === 'grid'}
          onClick={() => store.setViewMode('grid')}
          label="Grid view"
        >
          <IconLayoutGrid className="h-4 w-4" />
        </ViewButton>
        <ViewButton
          active={store.viewMode === 'list'}
          onClick={() => store.setViewMode('list')}
          label="List view"
        >
          <IconList className="h-4 w-4" />
        </ViewButton>
      </div>
    </div>
  );
}

function ViewButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-lg transition duration-200 ease-standard',
        active ? 'bg-accent text-white shadow' : 'text-fg-2 hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}
